import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'bow_use_hardware';

/**
 * Detecta se o app está rodando em ambiente de dev (hostname `dev.*`,
 * `localhost` ou `127.0.0.1`). Em prod retorna `isDev=false` e o toggle
 * fica oculto — o backend ignora o header X-Bow-Use-Hardware em produção
 * mesmo que o frontend mande.
 *
 * ⚠️ App nativo (Capacitor) é SEMPRE prod (aponta pra bow.app.br), mas o
 * webview roda em `https://localhost` — sem este guard o hostname `localhost`
 * faria o toggle de dev vazar pro app do cliente. Nativo nunca é dev.
 */
export function isDevHostname(): boolean {
  if (typeof window === 'undefined') return false;
  if (Capacitor.isNativePlatform()) return false;
  const h = window.location.hostname;
  return h.startsWith('dev.') || h === 'localhost' || h === '127.0.0.1';
}

/**
 * Hook do toggle dev "usar hardware real". Default false (= simulação
 * via mock_profile). Persistido em localStorage.
 *
 * - useHardware=false → frontend NÃO manda header → backend usa o default
 *   `BOW_HUB_USE_MOCK_PROFILE=true`. Hub simula MeterValues, relé NÃO liga.
 * - useHardware=true  → frontend manda `X-Bow-Use-Hardware: true` →
 *   backend não envia mock_profile pro Hub → relé do carregador físico LIGA.
 *
 * Em prod, isDev=false; o componente que usa o hook deve esconder o
 * controle. Mesmo se vazasse, backend ignora o header em prod.
 */
export function useDevHardwareToggle() {
  const isDev = isDevHostname();
  const [useHardware, setUseHardwareState] = useState<boolean>(() => {
    if (!isDev) return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  // Sync entre abas/janelas — se o user trocar em outra aba, atualiza aqui.
  useEffect(() => {
    if (!isDev) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setUseHardwareState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isDev]);

  const setUseHardware = (v: boolean) => {
    setUseHardwareState(v);
    if (isDev) {
      localStorage.setItem(STORAGE_KEY, String(v));
    }
  };

  return { isDev, useHardware, setUseHardware };
}

/**
 * Retorna o valor a passar como header `X-Bow-Use-Hardware` no
 * POST /charging/start, ou undefined se não deve mandar nada.
 *
 * Lê direto do localStorage pra ser usado fora de componentes React
 * (ex.: api/charging.ts).
 */
export function getUseHardwareHeader(): string | undefined {
  if (!isDevHostname()) return undefined;
  return localStorage.getItem(STORAGE_KEY) === 'true' ? 'true' : undefined;
}
